import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Table, Row, Col, Card, CardBody, Input, Badge,
    Pagination, PaginationItem, PaginationLink, Label
} from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";

// MUI DatePicker Imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const AgentPaymentList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // --- Core States ---
    const [payments, setPayments] = useState([]);
    const [summary, setSummary] = useState({
        total_entries: 0,
        paid_amount: 0,
        total_payable_amount: 0,
        balance_amount: 0
    });
    const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1 });

    // --- Filter States ---
    const [filters, setFilters] = useState({
        agent_name: "",
        payment_type: null, // React-select uses object
        from_date: null, // MUI initialized as null
        to_date: null,
        page: 1,
        limit: 10
    });

    const typeOptions = [
        { value: "purchase", label: "Purchase Basis" },
        { value: "amount", label: "Direct Amount" }
    ];

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '45px',
            height: '45px',
            borderRadius: '8px',
            borderColor: '#dee2e6',
            boxShadow: 'none',
            '&:hover': { borderColor: '#2e4a3d' }
        }),
        valueContainer: (provided) => ({ ...provided, height: '45px', padding: '0 6px' }),
        input: (provided) => ({ ...provided, margin: '0px' }),
        indicatorSeparator: () => ({ display: 'none' }),
        indicatorsContainer: (provided) => ({ ...provided, height: '45px' }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    useEffect(() => {
        loadPayments();
    }, [filters.page]);

    const loadPayments = async () => {
        dispatch(loadingTrue());
        try {
            const params = {
                ...filters,
                payment_type: filters.payment_type ? filters.payment_type.value : "",
                from_date: filters.from_date ? dayjs(filters.from_date).format('YYYY-MM-DD') : "",
                to_date: filters.to_date ? dayjs(filters.to_date).format('YYYY-MM-DD') : ""
            };
            const queryParams = new URLSearchParams(params).toString();
            const res = await Handler({ method: "GET", url: `/agents/list.php?${queryParams}` });

            if (res?.success) {
                setPayments(res.data || []);
                setSummary(res.summary);
                setPagination({
                    current_page: res.page,
                    total_pages: res.total_pages,
                    total_records: res.total
                });
                dispatch(loadingFalse());
            } else {
                toast.error(res?.message || "Failed to fetch data");
            }
        } catch (err) {
            toast.error("Server connection error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-1 bg-light min-vh-100">
                <div className="page-header mb-4">
                    <h6 className="text-muted text-uppercase small fw-bold">Financials</h6>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h3 className="fw-bold m-0 text-theme">Agent Commissions</h3>
                            <p className="text-muted small m-0">Tracking payments made from invoice profits.</p>
                        </div>
                        <Button className="add-btn px-4 shadow-sm" onClick={() => navigate("/agent-pay/add")}>
                            <i className="bx bx-plus me-1"></i> New Agent Payment
                        </Button>
                    </div>
                </div>

                {/* SUMMARY CARDS */}
                <Row className="mb-4 g-3">
                    <Col md={3}>
                        <Card className="border-0 shadow-sm rounded-4 bg-theme text-white text-center p-3">
                            <small className="opacity-75 text-uppercase fw-bold">Total Entries</small>
                            <h4 className="fw-bold mb-0">{summary.total_entries}</h4>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm rounded-4 text-center p-3 text-primary">
                            <small className="text-muted text-uppercase fw-bold">Total Payable</small>
                            <h4 className="fw-bold mb-0">₹ {Math.round(summary.total_payable_amount || 0)?.toLocaleString()}</h4>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm rounded-4 text-center p-3 text-success">
                            <small className="text-muted text-uppercase fw-bold">Total Paid</small>
                            <h4 className="fw-bold mb-0">₹ {Math.round(summary.paid_amount || 0)?.toLocaleString()}</h4>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm rounded-4 text-center p-3 text-danger">
                            <small className="text-muted text-uppercase fw-bold">Balance Amount</small>
                            <h4 className="fw-bold mb-0">₹ {Math.round(summary.balance_amount || 0)?.toLocaleString()}</h4>
                        </Card>
                    </Col>
                </Row>

                {/* FILTERS */}
                <Card className="border-0 shadow-sm mb-4 rounded-4">
                    <CardBody className="p-4">
                        <Row className="g-3 align-items-end">
                            <Col md={3}>
                                <Label className="small fw-bold">Agent Name</Label>
                                <Input
                                    style={{ height: '45px' }}
                                    placeholder="Search agent..."
                                    value={filters.agent_name}
                                    onChange={(e) => setFilters({ ...filters, agent_name: e.target.value })}
                                />
                            </Col>
                            <Col md={2}>
                                <Label className="small fw-bold">Type</Label>
                                <Select
                                    options={typeOptions}
                                    value={filters.payment_type}
                                    onChange={(opt) => setFilters({ ...filters, payment_type: opt, page: 1 })}
                                    styles={customSelectStyles}
                                    isClearable
                                    placeholder="Select"
                                />
                            </Col>
                            <Col md={2}>
                                <Label className="small fw-bold">From</Label>
                                <DatePicker
                                    value={filters.from_date}
                                    format="DD/MM/YYYY"
                                    disableFuture
                                    onChange={(val) => setFilters({ ...filters, from_date: val, page: 1 })}
                                    maxDate={dayjs()}
                                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { height: '45px', borderRadius: '8px' } } } }}
                                />
                            </Col>
                            <Col md={2}>
                                <Label className="small fw-bold">To</Label>
                                <DatePicker
                                    disableFuture
                                    format="DD/MM/YYYY"
                                    value={filters.to_date}
                                    onChange={(val) => setFilters({ ...filters, to_date: val, page: 1 })}
                                    minDate={filters.from_date}
                                    maxDate={dayjs()}
                                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { height: '45px', borderRadius: '8px' } } } }}
                                />
                            </Col>
                            <Col md={3}>
                                <Button color="primary" block className="fw-bold" style={{ height: '45px' }} onClick={() => { setFilters({ ...filters, page: 1 }); loadPayments(); }}>
                                    <i className="bx bx-filter-alt"></i> Apply Filter
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* PAYMENTS TABLE */}
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="bg-light text-muted small text-uppercase">
                                <tr>
                                    <th>S.No</th>
                                    <th className="ps-4">Date & ID</th>
                                    <th>Agent Details</th>
                                    <th>Invoice & Rate</th>
                                    <th>Ref. Weight</th>
                                    <th className="text-center">Payment Status</th>
                                    <th className="text-end pe-4">Current Paid</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.length > 0 ? (
                                    payments.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className="ps-4 align-middle">
                                                <div className="fw-bold">{((filters.page - 1) * filters.limit) + index + 1}</div>
                                            </td>
                                            <td className="ps-4">
                                                <div className="fw-bold text-dark">#{item.id}</div>
                                                <small className="text-muted">{dayjs(item.created_at).format('DD-MM-YYYY')}</small>
                                            </td>
                                            <td>
                                                <div className="fw-bold text-theme text-capitalize">{item.agent.name}</div>
                                                <small className="text-muted">{item.agent.phone || 'N/A'}</small>
                                            </td>
                                            <td>
                                                <div className="mb-1">
                                                    <Badge color="light" className="text-dark border small">
                                                        <i className="bx bx-file me-1"></i>{item.invoice.invoice_no}
                                                    </Badge>
                                                </div>
                                                <small className="text-muted">Rate Diff: </small>
                                                <span className="text-danger fw-bold small">₹{item.payment_summary.rate_difference}</span>
                                            </td>
                                            <td>
                                                <div className="fw-bold">{item.payment.reference_weight} KG</div>
                                                <small className="text-muted text-uppercase" style={{ fontSize: '10px' }}>{item.payment.type}</small>
                                            </td>
                                            <td className="text-center">
                                                <div className="small text-muted mb-1">Total: ₹{item.payment_summary.total_payable_amount}</div>
                                                <Badge color={item.payment_summary.balance_amount <= 0 ? "success" : "warning"} pill>
                                                    {item.payment_summary.balance_amount <= 0 ? "Fully Paid" : `Bal: ₹${item.payment_summary.balance_amount}`}
                                                </Badge>
                                            </td>
                                            <td className="text-end pe-4">
                                                <div className="fw-bold text-success fs-5">₹ {Math.round(item.payment.amount)?.toLocaleString()}</div>
                                                {item.note && <small className="text-muted d-block" style={{ fontSize: '10px' }}>{item.note}</small>}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="7" className="py-5 text-center text-muted">No agent payment records found.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {/* PAGINATION */}
                {pagination?.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <Pagination className="pagination-rounded shadow-sm">
                            <PaginationItem disabled={filters.page <= 1}>
                                <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters.page - 1 })} />
                            </PaginationItem>
                            {[...Array(pagination?.total_pages)].map((_, i) => (
                                <PaginationItem active={i + 1 === filters.page} key={i}>
                                    <PaginationLink onClick={() => setFilters({ ...filters, page: i + 1 })}>{i + 1}</PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={filters.page >= pagination?.total_pages}>
                                <PaginationLink next onClick={() => setFilters({ ...filters, page: filters.page + 1 })} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
        </LocalizationProvider>
    );
};

export default AgentPaymentList;